import { create } from "zustand";
import { toast } from "sonner";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  AGENT_SDK_THREAD_STATUS,
  AgentSdkAddMessageResponseSchema,
  AgentSdkMessage,
  AgentSdkMessageSchema,
  AgentSdkMessagesResponseSchema,
  AgentSdkQueueRunResponseSchema,
  AgentSdkThread,
  AgentSdkThreadResponseSchema,
  AgentSdkThreadsResponseSchema,
  AgentSdkThreadWaitTimer,
  AgentTask,
  CreateThreadTaskRequest,
  CreateThreadTaskResponseSchema,
  type TaskActionId,
  DEFAULT_MESSAGE_PAGE_SIZE,
  PublicUser,
  ThreadVisibilityResponseSchema,
  type ThreadVisibility,
  type ThreadVisibilityResponse,
  type UserViewContext,
} from "@forgepoint/shared";
import { z } from "zod";
import { useTeamsStore } from "@/store/teamsStore";
import {
  refreshEntitiesForToolResults,
  seedProcessedToolResults,
} from "@/modules/standardAgent/liveRefresh";

/**
 * Per-thread pagination bookkeeping. Sequences are `threadSequence` cursors;
 * `null` when a thread has no loaded messages yet.
 */
export interface ThreadMessagesMeta {
  /** More history exists before the oldest loaded message. */
  hasOlder: boolean;
  /** Oldest loaded `threadSequence` — cursor for the next older page. */
  oldestSequence: number | null;
  /** Newest loaded `threadSequence` — cursor for delta polling. */
  newestSequence: number | null;
  /** An older page fetch is in flight (guards scroll-triggered loads). */
  isLoadingOlder: boolean;
}

/** Task actions the thread panel can request for a quote review. */
type QuoteReviewActionId = Exclude<TaskActionId, "SendEmail">;

/** Status the approve endpoint returns when the approved email fails to send. */
const SEND_FAILED_STATUS = 502;

interface StandardAgentState {
  // Thread list
  threads: AgentSdkThread[];
  threadMap: Map<string, AgentSdkThread>;
  isLoadingThreads: boolean;
  threadsError: string | null;

  // Per-thread error (e.g. 403 access denied)
  threadError: Map<string, string | null>;

  // Messages by thread (ascending by threadSequence)
  messagesByThread: Map<string, AgentSdkMessage[]>;
  isLoadingMessages: Map<string, boolean>;
  messagesError: Map<string, string | null>;
  // Pagination cursor + load state per thread, driving scroll-up history loading
  messagesMetaByThread: Map<string, ThreadMessagesMeta>;

  // Open agent tasks by thread — DRAFT and PENDING_REVIEW only
  openTasksByThread: Map<string, AgentTask[]>;
  // Non-cancelled wait timers by thread
  waitTimersByThread: Map<string, AgentSdkThreadWaitTimer[]>;

  // Sending state
  isSending: boolean;
  sendError: string | null;

  // Task approval state
  approvingTaskId: string | null;
  denyingTaskId: string | null;
  sendErrorByTaskId: Map<string, string>;

  // Per-thread in-flight state for creating a quote review task

  // Retry state
  isRetrying: boolean;
  retryError: string | null;

  // User review output save state
  isSavingUserReviewOutput: Map<string, boolean>;
  userReviewOutputSaveError: Map<string, string | null>;

  // Polling
  pollingIntervals: Map<string, ReturnType<typeof setInterval>>;

  // Thread visibility data per thread
  threadVisibility: Map<string, ThreadVisibilityResponse>;
  threadVisibilityLoading: Map<string, boolean>;
  threadVisibilityUpdating: Map<string, boolean>;

  // Active thread tab filter — when set, only threads owned by this user are shown
  filterByUserId: string | null;
  // Active thread search term — persisted so refresh paths keep the filter
  filterBySearch: string | null;
}

interface StandardAgentActions {
  // Fetch operations
  fetchThreads: (params?: {
    limit?: number;
    offset?: number;
    userId?: string;
    search?: string;
  }) => Promise<void>;
  fetchThread: (threadId: string) => Promise<void>;
  updateThreadName: (threadId: string, name: string) => Promise<boolean>;
  fetchMessages: (threadId: string) => Promise<void>;
  fetchOlderMessages: (threadId: string) => Promise<void>;
  pollMessages: (threadId: string) => Promise<void>;

  // Create/add operations
  createThread: (
    message: string,
    metadata?: Record<string, unknown>,
    assetIds?: string[],
    visibility?: ThreadVisibility,
  ) => Promise<{ threadId: string; eventId: string } | null>;
  addMessage: (
    threadId: string,
    message: string,
    assetIds?: string[],
    viewContext?: UserViewContext,
  ) => Promise<{ eventId: string } | null>;

  // Polling - two-phase pattern
  pollUntilActive: (threadId: string, intervalMs?: number) => void;
  pollUntilCompletion: (threadId: string, intervalMs?: number) => void;
  stopPolling: (threadId: string) => void;
  stopAllPolling: () => void;

  // Task approval
  approveTask: (taskId: string, threadId: string) => Promise<boolean>;
  createQuoteReviewTask: (
    threadId: string,
    quoteId: string,
    actionId: QuoteReviewActionId,
  ) => Promise<string | null>;
  denyTask: (taskId: string, threadId: string) => Promise<boolean>;

  // Retry
  retryThread: (threadId: string) => Promise<boolean>;

  // Stop
  stopThread: (threadId: string) => Promise<boolean>;

  // Cancel wait timer
  cancelWaitTimer: (threadId: string) => Promise<boolean>;

  saveUserReviewOutput: (
    threadId: string,
    messageId: string,
    formJson: unknown,
    confirmedIndices?: number[],
  ) => Promise<boolean>;

  // Thread visibility
  fetchThreadVisibility: (threadId: string) => Promise<void>;
  updateThreadVisibility: (
    threadId: string,
    visibility: ThreadVisibility,
  ) => Promise<boolean>;
  addThreadVisibilityUser: (
    threadId: string,
    userId: string,
  ) => Promise<boolean>;
  removeThreadVisibilityUser: (
    threadId: string,
    userId: string,
  ) => Promise<string | null>;

  // Helpers
  getThreadById: (threadId: string) => AgentSdkThread | undefined;
  getMessagesByThreadId: (threadId: string) => AgentSdkMessage[];
}

const STOP_POLL_INTERVAL_MS = 500;

const initialState: StandardAgentState = {
  threads: [],
  threadMap: new Map(),
  isLoadingThreads: false,
  threadsError: null,

  threadError: new Map(),

  messagesByThread: new Map(),
  isLoadingMessages: new Map(),
  messagesError: new Map(),
  messagesMetaByThread: new Map(),
  openTasksByThread: new Map(),
  waitTimersByThread: new Map(),

  isSending: false,
  sendError: null,

  approvingTaskId: null,
  denyingTaskId: null,
  sendErrorByTaskId: new Map(),

  isRetrying: false,
  retryError: null,

  isSavingUserReviewOutput: new Map(),
  userReviewOutputSaveError: new Map(),

  pollingIntervals: new Map(),

  threadVisibility: new Map(),
  threadVisibilityLoading: new Map(),
  threadVisibilityUpdating: new Map(),

  filterByUserId: null,
  filterBySearch: null,
};

// Monotonic id for thread-list requests. Only the most recent request is
// allowed to write results, so slower earlier requests can't clobber the list
// with stale, out-of-order responses.
let latestThreadsRequestId = 0;

export const useStandardAgentStore = create<
  StandardAgentState & StandardAgentActions
>()((set, get) => ({
  ...initialState,

  fetchThreads: async (fetchParams?: {
    limit?: number;
    offset?: number;
    userId?: string;
    search?: string;
  }) => {
    const filterByUserId = fetchParams?.userId ?? null;
    const filterBySearch = fetchParams?.search?.trim() || null;
    const requestId = ++latestThreadsRequestId;
    set({
      isLoadingThreads: true,
      threadsError: null,
      filterByUserId,
      filterBySearch,
    });

    try {
      const params: Record<string, string> = {};
      if (fetchParams?.limit !== undefined) {
        params.limit = String(fetchParams.limit);
      }
      if (fetchParams?.offset !== undefined) {
        params.offset = String(fetchParams.offset);
      }
      if (filterByUserId) {
        params.userId = filterByUserId;
      }
      if (filterBySearch) {
        params.search = filterBySearch;
      }

      const response = await fetchApi("standard-agent/threads", "GET", {
        params,
      });

      if (requestId !== latestThreadsRequestId) {
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to fetch threads");
      }

      const parsed = AgentSdkThreadsResponseSchema.safeParse(response.data);
      if (!parsed.success) {
        throw new Error("Invalid threads response");
      }

      const threadMap = new Map<string, AgentSdkThread>();
      parsed.data.threads.forEach((thread) => {
        threadMap.set(thread.id, thread);
      });

      set({
        threads: parsed.data.threads,
        threadMap,
        isLoadingThreads: false,
      });
    } catch (error) {
      if (requestId !== latestThreadsRequestId) {
        return;
      }
      if (error instanceof Error) {
        captureException(error);
      }
      set({
        threadsError:
          error instanceof Error ? error.message : "Unknown error occurred",
        isLoadingThreads: false,
      });
    }
  },

  fetchThread: async (threadId: string) => {
    try {
      const response = await fetchApi(
        `standard-agent/threads/${threadId}`,
        "GET",
      );

      if (!response.ok) {
        set((state) => {
          const newThreadError = new Map(state.threadError);
          newThreadError.set(
            threadId,
            response.error ?? "Failed to fetch thread",
          );
          return { threadError: newThreadError };
        });
        return;
      }

      const parsed = AgentSdkThreadResponseSchema.safeParse(response.data);
      if (!parsed.success) {
        throw new Error("Invalid thread response");
      }

      set((state) => {
        const newThreadMap = new Map(state.threadMap);
        newThreadMap.set(threadId, parsed.data.thread);

        // Update threads array if thread exists there
        const newThreads = state.threads.map((t) =>
          t.id === threadId ? parsed.data.thread : t,
        );

        // Add to threads array if not present
        if (!state.threads.find((t) => t.id === threadId)) {
          newThreads.unshift(parsed.data.thread);
        }

        const newThreadError = new Map(state.threadError);
        newThreadError.set(threadId, null);

        return {
          threadMap: newThreadMap,
          threads: newThreads,
          threadError: newThreadError,
        };
      });
    } catch (error) {
      if (error instanceof Error) {
        captureException(error);
      }
    }
  },

  updateThreadName: async (threadId: string, name: string) => {
    try {
      const response = await fetchApi(
        `standard-agent/threads/${threadId}`,
        "PATCH",
        {
          body: { name },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update thread name");
      }

      const parsed = AgentSdkThreadResponseSchema.safeParse(response.data);
      if (!parsed.success) {
        throw new Error("Invalid thread response");
      }

      set((state) => {
        const existingThread = state.threadMap.get(threadId);
        const updatedThread = existingThread
          ? { ...existingThread, ...parsed.data.thread }
          : parsed.data.thread;
        const newThreadMap = new Map(state.threadMap);
        newThreadMap.set(threadId, updatedThread);
        const newThreads = state.threads.map((thread) =>
          thread.id === threadId ? updatedThread : thread,
        );

        return {
          threadMap: newThreadMap,
          threads: newThreads,
        };
      });

      return true;
    } catch (error) {
      if (error instanceof Error) {
        captureException(error);
      }
      return false;
    }
  },

  fetchMessages: async (threadId: string) => {
    const hadMessages = (get().messagesByThread.get(threadId)?.length ?? 0) > 0;
    set((state) => {
      const newIsLoadingMessages = new Map(state.isLoadingMessages);
      newIsLoadingMessages.set(threadId, true);
      const newMessagesError = new Map(state.messagesError);
      newMessagesError.set(threadId, null);
      return {
        isLoadingMessages: newIsLoadingMessages,
        messagesError: newMessagesError,
      };
    });

    try {
      const response = await fetchApi(
        `standard-agent/threads/${threadId}/messages?limit=${DEFAULT_MESSAGE_PAGE_SIZE}`,
        "GET",
      );

      if (!response.ok) {
        throw new Error("Failed to fetch messages");
      }

      const parsed = AgentSdkMessagesResponseSchema.safeParse(response.data);
      if (!parsed.success) {
        throw new Error("Invalid messages response");
      }

      const partialUsers = extractUsersFromMessages(parsed.data.messages);
      useTeamsStore.getState().mergeUsers(partialUsers);

      const bounds = computeSequenceBounds(parsed.data.messages);

      set((state) => {
        const newMessagesByThread = new Map(state.messagesByThread);
        newMessagesByThread.set(threadId, parsed.data.messages);
        const newIsLoadingMessages = new Map(state.isLoadingMessages);
        newIsLoadingMessages.set(threadId, false);
        const newOpenTasksByThread = new Map(state.openTasksByThread);
        newOpenTasksByThread.set(threadId, parsed.data.openTasks);
        const newWaitTimersByThread = new Map(state.waitTimersByThread);
        newWaitTimersByThread.set(threadId, parsed.data.waitTimers);
        const newMessagesMeta = new Map(state.messagesMetaByThread);
        newMessagesMeta.set(threadId, {
          hasOlder: parsed.data.hasOlder,
          oldestSequence: bounds.oldest,
          newestSequence: bounds.newest,
          isLoadingOlder: false,
        });
        return {
          messagesByThread: newMessagesByThread,
          isLoadingMessages: newIsLoadingMessages,
          openTasksByThread: newOpenTasksByThread,
          waitTimersByThread: newWaitTimersByThread,
          messagesMetaByThread: newMessagesMeta,
        };
      });

      // A refresh of an already-open thread can carry brand-new results (fast
      // turns finish between delta polls); a first load only seeds the dedup
      // set so history never triggers refetches.
      if (hadMessages) {
        refreshEntitiesForToolResults(
          threadId,
          parsed.data.messages,
          parsed.data.messages,
        );
      } else {
        seedProcessedToolResults(threadId, parsed.data.messages);
      }
    } catch (error) {
      if (error instanceof Error) {
        captureException(error);
      }
      set((state) => {
        const newIsLoadingMessages = new Map(state.isLoadingMessages);
        newIsLoadingMessages.set(threadId, false);
        const newMessagesError = new Map(state.messagesError);
        newMessagesError.set(
          threadId,
          error instanceof Error ? error.message : "Unknown error occurred",
        );
        return {
          isLoadingMessages: newIsLoadingMessages,
          messagesError: newMessagesError,
        };
      });
    }
  },

  fetchOlderMessages: async (threadId: string) => {
    const meta = get().messagesMetaByThread.get(threadId);
    if (
      !meta ||
      !meta.hasOlder ||
      meta.isLoadingOlder ||
      meta.oldestSequence === null
    ) {
      return;
    }

    const beforeSequence = meta.oldestSequence;

    set((state) => {
      const newMeta = new Map(state.messagesMetaByThread);
      newMeta.set(threadId, { ...meta, isLoadingOlder: true });
      return { messagesMetaByThread: newMeta };
    });

    try {
      const response = await fetchApi(
        `standard-agent/threads/${threadId}/messages?limit=${DEFAULT_MESSAGE_PAGE_SIZE}&beforeSequence=${beforeSequence}`,
        "GET",
      );

      if (!response.ok) {
        throw new Error("Failed to fetch older messages");
      }

      const parsed = AgentSdkMessagesResponseSchema.safeParse(response.data);
      if (!parsed.success) {
        throw new Error("Invalid messages response");
      }

      useTeamsStore
        .getState()
        .mergeUsers(extractUsersFromMessages(parsed.data.messages));

      const bounds = computeSequenceBounds(parsed.data.messages);

      set((state) => {
        const existing = state.messagesByThread.get(threadId) ?? [];
        const existingIds = new Set(existing.map((m) => m.id));
        const prepend = parsed.data.messages.filter(
          (m) => !existingIds.has(m.id),
        );

        const newMessagesByThread = new Map(state.messagesByThread);
        newMessagesByThread.set(threadId, [...prepend, ...existing]);

        const current = state.messagesMetaByThread.get(threadId);
        const newMeta = new Map(state.messagesMetaByThread);
        newMeta.set(threadId, {
          hasOlder: parsed.data.hasOlder,
          oldestSequence: bounds.oldest ?? current?.oldestSequence ?? null,
          newestSequence: current?.newestSequence ?? bounds.newest,
          isLoadingOlder: false,
        });
        return {
          messagesByThread: newMessagesByThread,
          messagesMetaByThread: newMeta,
        };
      });
      seedProcessedToolResults(threadId, parsed.data.messages);
    } catch (error) {
      if (error instanceof Error) {
        captureException(error);
      }
      set((state) => {
        const current = state.messagesMetaByThread.get(threadId);
        if (!current) {
          return {};
        }
        const newMeta = new Map(state.messagesMetaByThread);
        newMeta.set(threadId, { ...current, isLoadingOlder: false });
        return { messagesMetaByThread: newMeta };
      });
    }
  },

  pollMessages: async (threadId: string) => {
    const meta = get().messagesMetaByThread.get(threadId);
    const newestSequence = meta?.newestSequence;

    // No baseline yet (thread just opened via polling) — do a full initial load.
    if (newestSequence === undefined || newestSequence === null) {
      await get().fetchMessages(threadId);
      return;
    }

    // Delta poll: inclusive of the newest known sequence so a still-streaming
    // last message is re-fetched and upserted rather than missed. Transient
    // failures are swallowed so the interval keeps the last good state.
    const response = await fetchApi(
      `standard-agent/threads/${threadId}/messages?sinceSequence=${newestSequence}`,
      "GET",
    );
    if (!response.ok) {
      return;
    }

    const parsed = AgentSdkMessagesResponseSchema.safeParse(response.data);
    if (!parsed.success) {
      return;
    }

    const delta = parsed.data.messages;
    if (delta.length === 0) {
      return;
    }

    useTeamsStore.getState().mergeUsers(extractUsersFromMessages(delta));

    const bounds = computeSequenceBounds(delta);

    set((state) => {
      const existing = state.messagesByThread.get(threadId) ?? [];
      const deltaIds = new Set(delta.map((m) => m.id));
      // Drop the re-fetched tail (untouched messages keep their identity, so
      // React.memo skips them) and append the fresh delta.
      const merged = [...existing.filter((m) => !deltaIds.has(m.id)), ...delta];

      const newMessagesByThread = new Map(state.messagesByThread);
      newMessagesByThread.set(threadId, merged);

      const newOpenTasksByThread = new Map(state.openTasksByThread);
      newOpenTasksByThread.set(threadId, parsed.data.openTasks);
      const newWaitTimersByThread = new Map(state.waitTimersByThread);
      newWaitTimersByThread.set(threadId, parsed.data.waitTimers);

      const current = state.messagesMetaByThread.get(threadId);
      const newMeta = new Map(state.messagesMetaByThread);
      newMeta.set(threadId, {
        hasOlder: current?.hasOlder ?? false,
        oldestSequence: current?.oldestSequence ?? bounds.oldest,
        newestSequence: Math.max(
          newestSequence,
          bounds.newest ?? newestSequence,
        ),
        isLoadingOlder: current?.isLoadingOlder ?? false,
      });
      return {
        messagesByThread: newMessagesByThread,
        openTasksByThread: newOpenTasksByThread,
        waitTimersByThread: newWaitTimersByThread,
        messagesMetaByThread: newMeta,
      };
    });

    refreshEntitiesForToolResults(
      threadId,
      delta,
      get().messagesByThread.get(threadId) ?? [],
    );
  },

  createThread: async (
    message: string,
    metadata?: Record<string, unknown>,
    assetIds?: string[],
    visibility?: ThreadVisibility,
  ) => {
    set({ isSending: true, sendError: null });

    try {
      const response = await fetchApi("standard-agent/threads", "POST", {
        body: { message, metadata, assetIds, visibility },
      });

      if (!response.ok) {
        throw new Error("Failed to create thread");
      }

      const parsed = AgentSdkQueueRunResponseSchema.safeParse(response.data);
      if (!parsed.success) {
        throw new Error("Invalid create thread response");
      }

      set({ isSending: false });

      // Start polling for the new thread
      get().pollUntilActive(parsed.data.threadId);

      return parsed.data;
    } catch (error) {
      if (error instanceof Error) {
        captureException(error);
      }
      set({
        isSending: false,
        sendError:
          error instanceof Error ? error.message : "Unknown error occurred",
      });
      return null;
    }
  },

  addMessage: async (
    threadId: string,
    message: string,
    assetIds?: string[],
    viewContext?: UserViewContext,
  ) => {
    set({ isSending: true, sendError: null });

    try {
      const response = await fetchApi(
        `standard-agent/threads/${threadId}/messages`,
        "POST",
        {
          body: { message, assetIds, viewContext },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to add message");
      }

      const parsed = AgentSdkAddMessageResponseSchema.safeParse(response.data);
      if (!parsed.success) {
        throw new Error("Invalid add message response");
      }

      set({ isSending: false });

      // Start polling for the thread
      get().pollUntilActive(threadId);

      return parsed.data;
    } catch (error) {
      if (error instanceof Error) {
        captureException(error);
      }
      set({
        isSending: false,
        sendError:
          error instanceof Error ? error.message : "Unknown error occurred",
      });
      return null;
    }
  },

  pollUntilActive: (threadId: string, intervalMs: number = 1000) => {
    // Stop existing polling for this threadId if any
    const existingInterval = get().pollingIntervals.get(threadId);
    if (existingInterval) {
      clearInterval(existingInterval);
    }

    // Fetch immediately first
    get().fetchThread(threadId);
    get().fetchMessages(threadId);

    // Start new polling interval
    const interval = setInterval(async () => {
      // Fetch updated thread data
      await get().fetchThread(threadId);
      await get().pollMessages(threadId);

      const thread = get().threadMap.get(threadId);

      // Once the thread is active, transition to completion polling
      if (thread?.status === AGENT_SDK_THREAD_STATUS.ACTIVE) {
        get().stopPolling(threadId);
        get().pollUntilCompletion(threadId);
        return;
      }
      if (
        thread?.status === AGENT_SDK_THREAD_STATUS.COMPLETED ||
        thread?.status === AGENT_SDK_THREAD_STATUS.FAILED ||
        thread?.status === AGENT_SDK_THREAD_STATUS.INTERRUPTED
      ) {
        get().stopPolling(threadId);
        return;
      }
    }, intervalMs);

    // Store the interval
    set((state) => {
      const newPollingIntervals = new Map(state.pollingIntervals);
      newPollingIntervals.set(threadId, interval);
      return { pollingIntervals: newPollingIntervals };
    });
  },

  pollUntilCompletion: (threadId: string, intervalMs: number = 2000) => {
    // Stop existing polling for this threadId if any
    const existingInterval = get().pollingIntervals.get(threadId);
    if (existingInterval) {
      clearInterval(existingInterval);
    }

    // Start new polling interval
    const interval = setInterval(async () => {
      // Fetch updated thread and messages data before checking whether polling
      // should continue. This stops the interval as soon as a refresh observes a
      // terminal status instead of waiting for one more tick.
      await get().fetchThread(threadId);
      await get().pollMessages(threadId);

      const thread = get().threadMap.get(threadId);

      if (!thread || thread.status !== AGENT_SDK_THREAD_STATUS.ACTIVE) {
        get().stopPolling(threadId);
        // Refresh threads list, preserving the current tab and search filters
        get().fetchThreads({
          userId: get().filterByUserId ?? undefined,
          search: get().filterBySearch ?? undefined,
        });
        // Refresh visibility — the agent may have created tasks with
        // assignees, which adds users via bulkAddUsers on the backend.
        get().fetchThreadVisibility(threadId);
        return;
      }
    }, intervalMs);

    // Store the interval
    set((state) => {
      const newPollingIntervals = new Map(state.pollingIntervals);
      newPollingIntervals.set(threadId, interval);
      return { pollingIntervals: newPollingIntervals };
    });
  },

  stopPolling: (threadId: string) => {
    const interval = get().pollingIntervals.get(threadId);
    if (interval) {
      clearInterval(interval);
      set((state) => {
        const newPollingIntervals = new Map(state.pollingIntervals);
        newPollingIntervals.delete(threadId);
        return { pollingIntervals: newPollingIntervals };
      });
    }
  },

  stopAllPolling: () => {
    const intervals = get().pollingIntervals;
    intervals.forEach((interval) => clearInterval(interval));
    set({ pollingIntervals: new Map() });
  },

  createQuoteReviewTask: async (
    threadId: string,
    quoteId: string,
    actionId: QuoteReviewActionId,
  ) => {
    try {
      const body: CreateThreadTaskRequest = {
        actionParameters: { actionId, parameters: { quoteId } },
      };
      const response = await fetchApi(
        `standard-agent/threads/${threadId}/tasks`,
        "POST",
        { body },
      );
      if (!response.ok) {
        throw new Error("Failed to create quote review task");
      }
      const { task } = CreateThreadTaskResponseSchema.parse(response.data);
      set((state) => {
        const newOpenTasksByThread = new Map(state.openTasksByThread);
        const existing = newOpenTasksByThread.get(threadId) ?? [];
        const hasTask = existing.some((openTask) => openTask.id === task.id);
        newOpenTasksByThread.set(
          threadId,
          hasTask
            ? existing.map((openTask) =>
                openTask.id === task.id ? task : openTask,
              )
            : [...existing, task],
        );
        return { openTasksByThread: newOpenTasksByThread };
      });
      await get().fetchMessages(threadId);
      return task.id;
    } catch (error) {
      if (error instanceof Error) {
        captureException(error);
      }
      return null;
    }
  },

  approveTask: async (taskId: string, threadId: string) => {
    set({ approvingTaskId: taskId });
    setSendError(taskId, null);
    try {
      const response = await fetchApi(`tasks/${taskId}/approve`, "POST");
      if (!response.ok) {
        if (response.status >= 500) {
          captureException(new Error("Failed to approve task"));
        }
        const toastOptions = {
          description:
            response.error ||
            "Please try again. If the problem continues, contact support.",
          duration: 10000,
        };
        if (response.status === SEND_FAILED_STATUS) {
          setSendError(taskId, response.error ?? "Failed to send email");
          toast.error("Email failed to send", toastOptions);
        } else {
          toast.error("Could not approve task", toastOptions);
        }
        await get().fetchMessages(threadId);
        return false;
      }
      await get().fetchThread(threadId);
      await get().fetchMessages(threadId);
      get().pollUntilActive(threadId);
      return true;
    } catch (error) {
      if (error instanceof Error) {
        captureException(error);
      }
      toast.error("Could not approve task", {
        description:
          "Check your connection and try again. If the problem continues, contact support.",
        duration: 10000,
      });
      return false;
    } finally {
      set({ approvingTaskId: null });
    }
  },

  denyTask: async (taskId: string, threadId: string) => {
    set({ denyingTaskId: taskId });
    try {
      const response = await fetchApi(`tasks/${taskId}/deny`, "POST");
      if (!response.ok) {
        throw new Error("Failed to deny task");
      }
      await get().fetchThread(threadId);
      await get().fetchMessages(threadId);
      get().pollUntilActive(threadId);
      return true;
    } catch (error) {
      if (error instanceof Error) {
        captureException(error);
      }
      return false;
    } finally {
      set({ denyingTaskId: null });
    }
  },

  retryThread: async (threadId: string) => {
    set({ isRetrying: true, retryError: null });
    try {
      const response = await fetchApi(
        `standard-agent/threads/${threadId}/retry`,
        "POST",
      );

      if (!response.ok) {
        throw new Error("Failed to retry thread");
      }

      const parsed = AgentSdkAddMessageResponseSchema.safeParse(response.data);
      if (!parsed.success) {
        throw new Error("Invalid retry response");
      }

      set({ isRetrying: false });
      get().pollUntilActive(threadId);
      return true;
    } catch (error) {
      if (error instanceof Error) {
        captureException(error);
      }
      set({
        isRetrying: false,
        retryError:
          error instanceof Error ? error.message : "Unknown error occurred",
      });
      return false;
    }
  },

  stopThread: async (threadId: string) => {
    try {
      const response = await fetchApi(
        `standard-agent/threads/${threadId}/stop`,
        "POST",
      );

      if (!response.ok) {
        throw new Error("Failed to stop thread");
      }

      await get().fetchThread(threadId);
      // Poll faster than the 2s default until the abort lands.
      get().pollUntilCompletion(threadId, STOP_POLL_INTERVAL_MS);
      return true;
    } catch (error) {
      if (error instanceof Error) {
        captureException(error);
      }
      return false;
    }
  },

  /**
   * Cancels the active wait timer on a thread, transitioning it to COMPLETED.
   * @param threadId - The ID of the thread whose wait timer should be cancelled
   * @returns Whether the cancellation succeeded
   */
  cancelWaitTimer: async (threadId: string) => {
    try {
      const response = await fetchApi(
        `standard-agent/threads/${threadId}/cancel-wait-timer`,
        "POST",
      );

      if (!response.ok) {
        throw new Error("Failed to cancel wait timer");
      }

      await get().fetchThread(threadId);
      await get().fetchMessages(threadId);
      return true;
    } catch (error) {
      if (error instanceof Error) {
        captureException(error);
      }
      return false;
    }
  },

  saveUserReviewOutput: async (
    threadId: string,
    messageId: string,
    formJson: unknown,
    confirmedIndices?: number[],
  ) => {
    set((state) => {
      const newIsSaving = new Map(state.isSavingUserReviewOutput);
      const newErrors = new Map(state.userReviewOutputSaveError);
      newIsSaving.set(messageId, true);
      newErrors.set(messageId, null);
      return {
        isSavingUserReviewOutput: newIsSaving,
        userReviewOutputSaveError: newErrors,
      };
    });

    try {
      const response = await fetchApi(
        `standard-agent/messages/${messageId}/user-review-output`,
        "PATCH",
        {
          body: { formJson, confirmedIndices },
        },
      );

      if (!response.ok) {
        const errorMessage =
          typeof response.data === "object" &&
          response.data !== null &&
          "error" in response.data &&
          typeof response.data.error === "string"
            ? response.data.error
            : "Failed to save user review output";
        throw new Error(errorMessage);
      }

      const parsedResponse = z
        .object({
          outputMessage: AgentSdkMessageSchema,
        })
        .safeParse(response.data);

      if (!parsedResponse.success) {
        throw new Error("Invalid save user review response");
      }

      await get().fetchMessages(threadId);

      set((state) => {
        const newIsSaving = new Map(state.isSavingUserReviewOutput);
        newIsSaving.set(messageId, false);
        return {
          isSavingUserReviewOutput: newIsSaving,
        };
      });

      return true;
    } catch (error) {
      if (error instanceof Error) {
        captureException(error);
      }

      set((state) => {
        const newIsSaving = new Map(state.isSavingUserReviewOutput);
        const newErrors = new Map(state.userReviewOutputSaveError);
        newIsSaving.set(messageId, false);
        newErrors.set(
          messageId,
          error instanceof Error
            ? error.message
            : "Failed to save user review output",
        );
        return {
          isSavingUserReviewOutput: newIsSaving,
          userReviewOutputSaveError: newErrors,
        };
      });

      return false;
    }
  },

  /**
   * Fetches visibility and user list for a thread from the API.
   * @param threadId - The thread to fetch visibility for
   */
  fetchThreadVisibility: async (threadId: string) => {
    set((state) => {
      const newLoading = new Map(state.threadVisibilityLoading);
      newLoading.set(threadId, true);
      return { threadVisibilityLoading: newLoading };
    });

    try {
      const response = await fetchApi(
        `standard-agent/threads/${threadId}/visibility`,
        "GET",
      );

      if (!response.ok) {
        throw new Error("Failed to fetch thread visibility");
      }

      const parsed = ThreadVisibilityResponseSchema.safeParse(response.data);
      if (!parsed.success) {
        throw new Error("Invalid thread visibility response");
      }

      set((state) => {
        const newVisibility = new Map(state.threadVisibility);
        newVisibility.set(threadId, parsed.data);
        const newLoading = new Map(state.threadVisibilityLoading);
        newLoading.set(threadId, false);
        return {
          threadVisibility: newVisibility,
          threadVisibilityLoading: newLoading,
        };
      });
    } catch (error) {
      if (error instanceof Error) {
        captureException(error);
      }
      set((state) => {
        const newLoading = new Map(state.threadVisibilityLoading);
        newLoading.set(threadId, false);
        return { threadVisibilityLoading: newLoading };
      });
    }
  },

  /**
   * Updates the visibility mode for a thread.
   * @param threadId - The thread to update
   * @param visibility - The new visibility mode
   * @returns Whether the update succeeded
   */
  updateThreadVisibility: async (
    threadId: string,
    visibility: ThreadVisibility,
  ) => {
    set((state) => {
      const newUpdating = new Map(state.threadVisibilityUpdating);
      newUpdating.set(threadId, true);
      return { threadVisibilityUpdating: newUpdating };
    });

    try {
      const response = await fetchApi(
        `standard-agent/threads/${threadId}/visibility`,
        "PUT",
        { body: { visibility } },
      );

      if (!response.ok) {
        throw new Error("Failed to update thread visibility");
      }

      // Re-fetch to get the updated user list and visibility
      await get().fetchThreadVisibility(threadId);

      // Update the thread's visibility in the thread map
      set((state) => {
        const newThreadMap = new Map(state.threadMap);
        const thread = newThreadMap.get(threadId);
        if (thread) {
          newThreadMap.set(threadId, { ...thread, visibility });
        }
        const newThreads = state.threads.map((t) =>
          t.id === threadId ? { ...t, visibility } : t,
        );
        const newUpdating = new Map(state.threadVisibilityUpdating);
        newUpdating.set(threadId, false);
        return {
          threadMap: newThreadMap,
          threads: newThreads,
          threadVisibilityUpdating: newUpdating,
        };
      });

      return true;
    } catch (error) {
      if (error instanceof Error) {
        captureException(error);
      }
      set((state) => {
        const newUpdating = new Map(state.threadVisibilityUpdating);
        newUpdating.set(threadId, false);
        return { threadVisibilityUpdating: newUpdating };
      });
      return false;
    }
  },

  /**
   * Adds a user to a private thread's visibility list.
   * @param threadId - The thread to add the user to
   * @param userId - The user to add
   * @returns Whether the add succeeded
   */
  addThreadVisibilityUser: async (threadId: string, userId: string) => {
    try {
      const response = await fetchApi(
        `standard-agent/threads/${threadId}/visibility/users`,
        "POST",
        { body: { userId } },
      );

      if (!response.ok) {
        throw new Error("Failed to add user to thread");
      }

      await get().fetchThreadVisibility(threadId);
      return true;
    } catch (error) {
      if (error instanceof Error) {
        captureException(error);
      }
      return false;
    }
  },

  /**
   * Removes a user from a private thread's visibility list.
   * @param threadId - The thread to remove the user from
   * @param userId - The user to remove
   * @returns Null on success, or an error message string on failure
   */
  removeThreadVisibilityUser: async (threadId: string, userId: string) => {
    try {
      const response = await fetchApi(
        `standard-agent/threads/${threadId}/visibility/users/${userId}`,
        "DELETE",
      );

      if (!response.ok) {
        const errorMessage =
          typeof response.data === "object" &&
          response.data !== null &&
          "error" in response.data &&
          typeof response.data.error === "string"
            ? response.data.error
            : "Failed to remove user from thread";
        throw new Error(errorMessage);
      }

      await get().fetchThreadVisibility(threadId);
      return null;
    } catch (error) {
      if (error instanceof Error) {
        captureException(error);
        return error.message;
      }
      return "Failed to remove user from thread";
    }
  },

  getThreadById: (threadId: string) => {
    return get().threadMap.get(threadId);
  },

  getMessagesByThreadId: (threadId: string) => {
    return get().messagesByThread.get(threadId) || [];
  },
}));

/**
 * Oldest/newest `threadSequence` across a message page, used to seed and advance
 * the pagination cursors. Returns `null` bounds when no message carries a
 * sequence (e.g. an empty page).
 */
function computeSequenceBounds(messages: AgentSdkMessage[]): {
  oldest: number | null;
  newest: number | null;
} {
  const sequences = messages
    .map((message) => message.threadSequence)
    .filter((sequence): sequence is number => sequence !== undefined);

  if (sequences.length === 0) {
    return { oldest: null, newest: null };
  }

  return {
    oldest: Math.min(...sequences),
    newest: Math.max(...sequences),
  };
}

/** Extracts unique user objects from hydrated messages. */
function extractUsersFromMessages(messages: AgentSdkMessage[]): PublicUser[] {
  const seen = new Set<string>();
  const users: PublicUser[] = [];
  for (const message of messages) {
    if (message.user && !seen.has(message.user.id)) {
      seen.add(message.user.id);
      users.push(message.user);
    }
  }
  return users;
}

/**
 * Sets or clears the email send failure reason shown on a SendEmail task's review.
 * @param taskId - The task whose send failed
 * @param reason - The failure reason, or null to clear it
 */
function setSendError(taskId: string, reason: string | null): void {
  useStandardAgentStore.setState((state) => {
    const sendErrorByTaskId = new Map(state.sendErrorByTaskId);
    if (reason === null) {
      sendErrorByTaskId.delete(taskId);
    } else {
      sendErrorByTaskId.set(taskId, reason);
    }
    return { sendErrorByTaskId };
  });
}
